<template>
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-9">
        <div class="card border-0 shadow">
          <!-- Header -->
          <div
            class="card-header bg-primary bg-gradient text-white p-4 border-0"
          >
            <h3 class="mb-0 fw-light text-center">Create Your Account</h3>
            <!-- <p class="text-center mb-0 mt-2 text-white-50">Please fill in the information below</p> -->
          </div>

          <div class="card-body p-4 p-md-5" v-if="json_data">
            <form
              @submit.prevent="registerPerson"
              class="needs-validation"
              novalidate
            >
              <!-- Validation Notice -->
              <div class="alert custom-alert-info mb-4" role="alert">
                <i class="bi bi-info-circle-fill me-2" aria-hidden="true"></i>
                Please note: Only English characters are allowed in all text
                fields.
              </div>

              <!-- Personal Information Section -->
              <div class="mb-5">
                <div class="d-flex align-items-center mb-4">
                  <h5 class="mb-0">Personal Information</h5>
                </div>

                <div class="row g-4">
                  <div class="col-md-6">
                    <label for="firstName" class="form-label pt-3 fw-semibold"
                      >First Name <span class="text-danger">*</span></label
                    >
                    <input
                      type="text"
                      class="form-control form-control-lg bg-light"
                      id="firstName"
                      v-model="json_data.firstName"
                      @input="validateEnglishChars('firstName')"
                      required
                      :aria-describedby="
                        touchedFields.firstName && validationErrors.firstName
                          ? 'firstNameError'
                          : null
                      "
                      autocomplete="given-name"
                      :aria-invalid="
                        touchedFields.firstName && validationErrors.firstName
                          ? 'true'
                          : 'false'
                      "
                    />
                    <div
                      class="text-danger small"
                      v-if="
                        touchedFields.firstName && validationErrors.firstName
                      "
                      id="firstNameError"
                    >
                      {{ validationErrors.firstName }}
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label for="lastName" class="form-label pt-3 fw-semibold"
                      >Last Name <span class="text-danger">*</span></label
                    >
                    <input
                      type="text"
                      class="form-control form-control-lg bg-light"
                      id="lastName"
                      v-model="json_data.lastName"
                      @input="validateEnglishChars('lastName')"
                      required
                      :aria-describedby="
                        touchedFields.lastName && validationErrors.lastName
                          ? 'lastNameError'
                          : null
                      "
                      autocomplete="family-name"
                      :aria-invalid="
                        touchedFields.lastName && validationErrors.lastName
                          ? 'true'
                          : 'false'
                      "
                    />
                    <div
                      class="text-danger small"
                      v-if="touchedFields.lastName && validationErrors.lastName"
                      id="lastNameError"
                    >
                      {{ validationErrors.lastName }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Account Information Section -->
              <div class="mb-5">
                <div class="d-flex align-items-center mb-4">
                  <h5 class="mb-0">Account Information</h5>
                </div>

                <div class="row g-4">
                  <div class="col-md-6">
                    <label for="username" class="form-label pt-3 fw-semibold"
                      >Username <span class="text-danger">*</span></label
                    >
                    <div class="input-group">
                      <span class="input-group-text bg-light border-end-0">
                        <i class="bi bi-person-badge" aria-hidden="true"></i>
                      </span>
                      <input
                        type="text"
                        class="form-control form-control-lg bg-light border-start-0"
                        id="username"
                        v-model="json_data.username"
                        @input="validateEnglishChars('username')"
                        required
                        :aria-describedby="
                          touchedFields.username && validationErrors.username
                            ? 'usernameError'
                            : null
                        "
                        autocomplete="username"
                        :aria-invalid="
                          touchedFields.username && validationErrors.username
                            ? 'true'
                            : 'false'
                        "
                      />
                    </div>
                    <div
                      class="text-danger small"
                      v-if="touchedFields.username && validationErrors.username"
                      id="usernameError"
                    >
                      {{ validationErrors.username }}
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label for="email" class="form-label pt-3 fw-semibold"
                      >Email <span class="text-danger">*</span></label
                    >
                    <div class="input-group">
                      <span class="input-group-text bg-light border-end-0">
                        <i class="bi bi-envelope" aria-hidden="true"></i>
                      </span>
                      <input
                        type="email"
                        class="form-control form-control-lg bg-light border-start-0"
                        id="email"
                        v-model="json_data.email"
                        @input="validateEmail"
                        required
                        :aria-describedby="
                          touchedFields.email && validationErrors.email
                            ? 'emailError'
                            : null
                        "
                        autocomplete="email"
                        :aria-invalid="
                          touchedFields.email && validationErrors.email
                            ? 'true'
                            : 'false'
                        "
                      />
                    </div>
                    <div
                      class="text-danger small"
                      v-if="touchedFields.email && validationErrors.email"
                      id="emailError"
                    >
                      {{ validationErrors.email }}
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label for="password" class="form-label pt-3 fw-semibold"
                      >Password <span class="text-danger">*</span></label
                    >
                    <div class="input-group">
                      <span class="input-group-text bg-light border-end-0">
                        <i class="bi bi-key" aria-hidden="true"></i>
                      </span>
                      <input
                        type="password"
                        class="form-control form-control-lg bg-light border-start-0"
                        id="password"
                        v-model="json_data.password"
                        @input="validateEnglishChars('password')"
                        required
                        :aria-describedby="getPasswordAriaDescribedBy()"
                        :aria-invalid="
                          passwordError ||
                          (touchedFields.password && validationErrors.password)
                            ? 'true'
                            : 'false'
                        "
                      />
                    </div>
                    <div
                      class="form-text"
                      id="passwordHelp"
                      v-if="!passwordError && !validationErrors.password"
                    >
                      Must be between 6 and 10 characters
                    </div>
                    <div
                      class="text-danger small"
                      v-if="passwordError"
                      id="passwordError"
                    >
                      {{ passwordError }}
                    </div>
                    <div
                      class="text-danger small"
                      v-if="touchedFields.password && validationErrors.password"
                      id="passwordValidationError"
                    >
                      {{ validationErrors.password }}
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label
                      for="password_confirm"
                      class="form-label pt-3 fw-semibold"
                      >Confirm Password
                      <span class="text-danger">*</span></label
                    >
                    <div class="input-group">
                      <span class="input-group-text bg-light border-end-0">
                        <i class="bi bi-key-fill" aria-hidden="true"></i>
                      </span>
                      <input
                        type="password"
                        class="form-control form-control-lg bg-light border-start-0"
                        id="password_confirm"
                        v-model="json_data.password_confirm"
                        @input="validateEnglishChars('password_confirm')"
                        required
                        :aria-describedby="getConfirmPasswordAriaDescribedBy()"
                        :aria-invalid="
                          confirmPasswordError ||
                          (touchedFields.password_confirm &&
                            validationErrors.password_confirm)
                            ? 'true'
                            : 'false'
                        "
                      />
                    </div>
                    <div
                      class="text-danger small"
                      v-if="confirmPasswordError"
                      id="passwordConfirmError"
                    >
                      {{ confirmPasswordError }}
                    </div>
                    <div
                      class="text-danger small"
                      v-if="
                        touchedFields.password_confirm &&
                        validationErrors.password_confirm
                      "
                      id="passwordConfirmValidationError"
                    >
                      {{ validationErrors.password_confirm }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Professional Information Section -->
              <div class="mb-5">
                <div class="d-flex align-items-center mb-4">
                  <h5 class="mb-0">Professional Information</h5>
                </div>

                <div class="row g-4">
                  <div class="col-md-6">
                    <label
                      for="institutionName"
                      class="form-label pt-3 fw-semibold"
                      >Institution Name
                      <span class="text-danger">*</span></label
                    >
                    <input
                      type="text"
                      class="form-control form-control-lg bg-light"
                      id="institutionName"
                      v-model="json_data.institutionName"
                      @input="validateEnglishChars('institutionName')"
                      required
                      :aria-describedby="
                        touchedFields.institutionName &&
                        validationErrors.institutionName
                          ? 'institutionNameError'
                          : null
                      "
                      :aria-invalid="
                        touchedFields.institutionName &&
                        validationErrors.institutionName
                          ? 'true'
                          : 'false'
                      "
                    />
                    <div
                      class="text-danger small"
                      v-if="
                        touchedFields.institutionName &&
                        validationErrors.institutionName
                      "
                      id="institutionNameError"
                    >
                      {{ validationErrors.institutionName }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location Information Section -->
              <div class="mb-5">
                <div class="d-flex align-items-center mb-4">
                  <h5 class="mb-0">Location Information</h5>
                </div>

                <div class="row g-4">
                  <div class="col-md-6">
                    <label for="country" class="form-label pt-3 fw-semibold"
                      >Country/Region <span class="text-danger">*</span></label
                    >
                    <select
                      class="form-select form-select-lg bg-light"
                      id="country"
                      v-model="json_data.country"
                      @change="onCountryChange"
                      required
                      :aria-describedby="
                        touchedFields.country && validationErrors.country
                          ? 'countryError'
                          : null
                      "
                      :aria-invalid="
                        touchedFields.country && validationErrors.country
                          ? 'true'
                          : 'false'
                      "
                    >
                      <option
                        v-for="countryOption in countries"
                        :key="countryOption.value"
                        :value="countryOption.value"
                      >
                        {{ countryOption.text }}
                      </option>
                    </select>
                    <div
                      class="text-danger small"
                      v-if="touchedFields.country && validationErrors.country"
                      id="countryError"
                    >
                      {{ validationErrors.country }}
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label
                      for="stateProvince"
                      class="form-label pt-3 fw-semibold"
                      >State/Province <span class="text-danger">*</span></label
                    >
                    <select
                      class="form-select form-select-lg bg-light"
                      id="stateProvince"
                      v-model="json_data.state"
                      @change="validateState"
                      required
                      :aria-describedby="
                        touchedFields.state && validationErrors.state
                          ? 'stateError'
                          : null
                      "
                      :aria-invalid="
                        touchedFields.state && validationErrors.state
                          ? 'true'
                          : 'false'
                      "
                    >
                      <option value="">Select State/Province</option>
                      <option
                        v-for="stateOption in statesProvinces"
                        :key="stateOption.value"
                        :value="stateOption.value"
                      >
                        {{ stateOption.text }}
                      </option>
                    </select>
                    <div
                      class="text-danger small"
                      v-if="touchedFields.state && validationErrors.state"
                      id="stateError"
                    >
                      {{ validationErrors.state }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Preferences Section -->
              <div class="mb-5">
                <div class="d-flex align-items-start mb-4">
                  <h5 class="mb-0">Preferences</h5>
                </div>

                <div class="row justify-content-start">
                  <div class="col-12 text-start">
                    <div class="d-flex flex-column align-items-start">
                      <div class="form-check mb-3">
                        <input
                          type="checkbox"
                          class="form-check-input"
                          id="okMail"
                          v-model="json_data.okMail"
                        />
                        <label class="form-check-label" for="okMail">
                          Get occasional TAIR newsletters and updates
                        </label>
                      </div>

                      <div class="form-check">
                        <input
                          type="checkbox"
                          class="form-check-input"
                          id="okEmailDisplay"
                          v-model="json_data.okEmailDisplay"
                        />
                        <label class="form-check-label" for="okEmailDisplay">
                          Display email address on Person detail page
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Alerts -->
              <div
                v-if="successMessage"
                class="alert alert-success border-0 bg-success bg-opacity-10"
                role="alert"
              >
                <i class="bi bi-check-circle-fill me-2" aria-hidden="true"></i>
                {{ successMessage }}
              </div>

              <div
                v-if="formErrors.length"
                class="alert alert-danger py-3 px-4 shadow-sm rounded-3 border-start border-danger border-4"
              >
                <!-- Simple Header -->
                <div class="d-flex align-items-center mb-2">
                  <h5 class="mb-0">Please fix the following errors:</h5>
                </div>

                <!-- Simple Error List -->
                <div class="ms-4">
                  <div
                    v-for="error in formErrors"
                    :key="error"
                    class="d-flex align-items-baseline mb-1"
                  >
                    <div class="text-danger me-2">•</div>
                    <div v-html="convertUrlsToLinks(error)"></div>
                  </div>
                </div>
              </div>

              <!-- Required Fields Note -->
              <div class="text-muted mb-4">
                <small
                  ><span class="text-danger">*</span> Required fields</small
                >
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                class="btn btn-primary btn-lg w-100"
                :disabled="isLoading"
              >
                <span
                  v-if="isLoading"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                <i
                  v-else
                  class="bi bi-person-plus-fill me-2"
                  aria-hidden="true"
                ></i>
                {{ isLoading ? "Creating Account..." : "Create Account" }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
//http://localhost:8082/register
import BaseEntry from "@/components/detail/BaseEntry";
import { mapGetters } from "vuex";
import Api from "@/services/Api";
import { Country, State } from "country-state-city";

export default {
  name: "RegistrationDetail",
  components: {
    BaseEntry,
  },
  data() {
    return {
      json_data: {
        username: "",
        password: "",
        password_confirm: "",
        email: "",
        okMail: false,
        okGeneSymbol: false,
        okEmailDisplay: false,
        firstName: "",
        lastName: "",
        institutionName: "",
        country: "",
        state: "",
      },
      formErrors: [],
      formattedAddress: "",
      loading: false,
      loadingError: false,
      successMessage: "",
      isLoading: false,
      passwordError: "",
      confirmPasswordError: "",
      validationErrors: {},
      touchedFields: {}, // Track which fields the user has interacted with
      countries: [],
      statesProvinces: [],
    };
  },
  computed: {
    hasValidationErrors() {
      return (
        Object.keys(this.validationErrors).length > 0 ||
        this.passwordError !== "" ||
        this.confirmPasswordError !== ""
      );
    },
  },
  async mounted() {
    document.title = "Person Registration";
    const link = document.createElement("link");
    link.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Load countries and set USA as default
    this.loadCountries();

    // No validation on initial load - wait for user interaction
  },
  watch: {
    "json_data.password"(newVal) {
      this.passwordError = "";
      if (newVal.length > 0 && (newVal.length < 6 || newVal.length > 10)) {
        this.passwordError = "Password must be between 6 and 10 characters.";
      }
    },
    "json_data.password_confirm"(newVal) {
      this.confirmPasswordError = "";
      if (newVal !== this.json_data.password) {
        this.confirmPasswordError = "Passwords do not match.";
      }
    },
  },
  methods: {
    ...mapGetters("authProfile", ["getLoginUrl", "getForgotUsernameUrl"]),

    // Load countries from the library
    loadCountries() {
      try {
        const allCountries = Country.getAllCountries();
        this.countries = allCountries.map((country) => ({
          text: country.name,
          value: country.name,
          isoCode: country.isoCode,
        }));

        // Set USA as default and load its states
        this.json_data.country = "United States";
        this.loadStatesForCountry("US");
      } catch (error) {
        console.error("Error loading countries:", error);
        // Fallback to basic list if library fails
        this.countries = [
          { text: "United States", value: "United States", isoCode: "US" },
          { text: "Canada", value: "Canada", isoCode: "CA" },
          { text: "United Kingdom", value: "United Kingdom", isoCode: "GB" },
          { text: "Other", value: "Other", isoCode: "OTHER" },
        ];
        this.json_data.country = "United States";
        this.loadStatesForCountry("US");
      }
    },

    // Load states/provinces based on selected country
    loadStatesForCountry(countryIsoCode) {
      try {
        if (!countryIsoCode || countryIsoCode === "OTHER") {
          this.statesProvinces = [{ text: "Not Applicable", value: "N/A" }];
          return;
        }

        const states = State.getStatesOfCountry(countryIsoCode);
        if (states && states.length > 0) {
          this.statesProvinces = states.map((state) => ({
            text: state.name,
            value: state.name,
          }));
        } else {
          // If no states available, provide generic options
          this.statesProvinces = [{ text: "Not Applicable", value: "N/A" }];
        }
      } catch (error) {
        console.error("Error loading states:", error);
        this.statesProvinces = [{ text: "Not Applicable", value: "N/A" }];
      }
    },

    // Handle country selection change
    onCountryChange() {
      const selectedCountry = this.countries.find(
        (c) => c.value === this.json_data.country
      );
      if (selectedCountry) {
        this.loadStatesForCountry(selectedCountry.isoCode);
        // Reset state selection when country changes
        this.json_data.state = "";
      }
    },

    // Validate for English characters only
    validateEnglishChars(field) {
      // Mark this field as touched
      this.$set(this.touchedFields, field, true);

      const value = this.json_data[field];
      // Delete the error if the field is empty or if we're setting it to empty
      if (!value || value.length === 0) {
        this.$set(this.validationErrors, field, "This field is required.");
        return;
      }

      // Check for non-English characters using regex
      // This regex allows English letters, numbers, spaces, and common punctuation
      const englishOnlyRegex =
        /^[A-Za-z0-9\s.,!?'"`@#$%^&*()_\-+=:;{}[\]|\\/<>~]+$/;

      if (!englishOnlyRegex.test(value)) {
        this.$set(
          this.validationErrors,
          field,
          "Only English characters are allowed in this form."
        );
      } else {
        // Clear the error for this field
        this.$delete(this.validationErrors, field);
      }
    },

    // Validate Email Format
    validateEmail() {
      // Mark email as touched
      this.$set(this.touchedFields, "email", true);

      const email = this.json_data.email;
      if (!email || email.length === 0) {
        this.$set(this.validationErrors, "email", "Email address is required.");
        return;
      }

      // Basic email validation regex
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      if (!emailRegex.test(email)) {
        this.$set(
          this.validationErrors,
          "email",
          "Please enter a valid email address in English (e.g., example@domain.com)."
        );
      } else {
        // Clear the error for this field
        this.$delete(this.validationErrors, "email");
      }
    },

    // Validate State Selection
    validateState() {
      // Mark this field as touched
      this.$set(this.touchedFields, "state", true);

      const value = this.json_data.state;
      // Check if a valid option is selected
      if (!value || value === "") {
        this.$set(
          this.validationErrors,
          "state",
          "State/Province is required."
        );
      } else {
        // Clear the error for this field
        this.$delete(this.validationErrors, "state");
      }
    },

    // Check all fields for validation before submitting
    validateAllFields() {
      const requiredTextFields = [
        "firstName",
        "lastName",
        "username",
        "password",
        "password_confirm",
        "institutionName",
      ];

      const requiredDropdownFields = ["state", "country"];

      // Mark all fields as touched during form submission
      requiredTextFields.forEach((field) => {
        this.$set(this.touchedFields, field, true);
      });
      requiredDropdownFields.forEach((field) => {
        this.$set(this.touchedFields, field, true);
      });
      this.$set(this.touchedFields, "email", true);

      // Clear any previous validation errors
      this.validationErrors = {};

      // Validate required text fields
      requiredTextFields.forEach((field) => {
        if (!this.json_data[field] || this.json_data[field].trim() === "") {
          this.$set(
            this.validationErrors,
            field,
            `${this.getFieldLabel(field)} is required.`
          );
        } else {
          // Check for non-English characters
          const englishOnlyRegex =
            /^[A-Za-z0-9\s.,!?'"`@#$%^&*()_\-+=:;{}[\]|\\/<>~]+$/;
          if (!englishOnlyRegex.test(this.json_data[field])) {
            this.$set(
              this.validationErrors,
              field,
              `Only English characters are allowed in ${this.getFieldLabel(
                field
              )}.`
            );
          }
        }
      });

      // Validate required dropdown fields
      requiredDropdownFields.forEach((field) => {
        if (!this.json_data[field] || this.json_data[field] === "") {
          this.$set(
            this.validationErrors,
            field,
            `${this.getFieldLabel(field)} is required.`
          );
        }
      });

      // Validate email
      const email = this.json_data.email;
      if (!email || email.length === 0) {
        this.$set(this.validationErrors, "email", "Email address is required.");
      } else {
        // Basic email validation regex
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (!emailRegex.test(email)) {
          this.$set(
            this.validationErrors,
            "email",
            "Please enter a valid email address in English (e.g., example@domain.com)."
          );
        }
      }

      return Object.keys(this.validationErrors).length === 0;
    },

    // Helper method to get human-readable field labels
    getFieldLabel(field) {
      const labels = {
        firstName: "First Name",
        lastName: "Last Name",
        username: "Username",
        password: "Password",
        password_confirm: "Confirm Password",
        institutionName: "Institution Name",
        state: "State/Province",
        country: "Country/Region",
      };

      return labels[field] || field.charAt(0).toUpperCase() + field.slice(1);
    },

    async registerPerson() {
      this.formErrors = [];

      // Collect all validation errors
      const allErrors = [];

      // Check password-specific errors
      if (this.passwordError) {
        allErrors.push(this.passwordError);
      }

      if (this.confirmPasswordError) {
        allErrors.push(this.confirmPasswordError);
      }

      // Validate all fields
      this.validateAllFields();

      // Add all validation errors to our errors list
      Object.values(this.validationErrors).forEach((error) => {
        allErrors.push(error);
      });

      // If we have any errors, show them and stop form submission
      if (allErrors.length > 0) {
        this.formErrors = allErrors;
        return;
      }

      this.isLoading = true;

      try {
        const registerDetails = this.json_data;
        const response = await Api().post(
          "user/register-person",
          registerDetails
        );
        this.isLoading = false;

        if (response.status === 200) {
          this.formErrors = [];
          this.successMessage = "Person registered successfully.";

          setTimeout(() => {
            this.successMessage = "";
            window.open(this.getLoginUrl(), "_self");
          }, 2000);
        }
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data) {
          let errorMessage = "";
          if (
            typeof error.response.data === "object" &&
            error.response.data.error
          ) {
            errorMessage = error.response.data.error;
          } else if (typeof error.response.data === "string") {
            errorMessage = error.response.data;
          }

          // Handle specific error codes
          if (errorMessage === "EMAIL_EXISTS") {
            errorMessage = `This email address is already registered. If you forgot your username, you can recover it here: ${this.getForgotUsernameUrl()}`;
          }

          this.formErrors.push(errorMessage);
        } else {
          this.formErrors.push(
            "An unexpected error occurred. Please try again."
          );
        }
      }
    },

    dateToYMD(dateTs) {
      let date = new Date(dateTs);
      const d = date.getDate();
      const m = date.getMonth() + 1;
      const y = date.getFullYear();
      return `${y}-${m.toString().padStart(2, "0")}-${d
        .toString()
        .padStart(2, "0")}`;
    },

    // Helper method for password aria-describedby
    getPasswordAriaDescribedBy() {
      const describedBy = [];

      // Always include help text when no errors are shown
      if (!this.passwordError && !this.validationErrors.password) {
        describedBy.push("passwordHelp");
      }

      // Include error messages when they exist
      if (this.passwordError) {
        describedBy.push("passwordError");
      }

      if (this.touchedFields.password && this.validationErrors.password) {
        describedBy.push("passwordValidationError");
      }

      return describedBy.length > 0 ? describedBy.join(" ") : null;
    },

    // Helper method for confirm password aria-describedby
    getConfirmPasswordAriaDescribedBy() {
      const describedBy = [];

      // Include error messages when they exist
      if (this.confirmPasswordError) {
        describedBy.push("passwordConfirmError");
      }

      if (
        this.touchedFields.password_confirm &&
        this.validationErrors.password_confirm
      ) {
        describedBy.push("passwordConfirmValidationError");
      }

      return describedBy.length > 0 ? describedBy.join(" ") : null;
    },

    // Convert URLs in error messages to clickable links
    convertUrlsToLinks(text) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      return text.replace(
        urlRegex,
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary text-decoration-underline">$1</a>'
      );
    },
  },
};
</script>

<style scoped>
.custom-alert-info {
  background-color: #e3f2fd !important; /* Accessible light blue */
  color: #084298 !important; /* Accessible dark blue */
  border: none;
}
.text-danger {
  font-size: 0.85rem;
  margin-top: 0.25rem;
}
.text-dark {
  color: #262a2f;
}
.form-control,
.form-select,
.input-group-text {
  border-color: #6c757d; /* Gray-600 */
}

/* Ensure form-select matches form-control height and appearance */
.form-select-lg {
  height: calc(1.5em + 1.5rem + 2px); /* Match form-control-lg height */
  padding: 0.75rem 2.25rem 0.75rem 1rem; /* Match form-control-lg padding with space for dropdown arrow */
  font-size: 1rem;
  line-height: 1.5;
  background-color: #f8f9fa; /* Match bg-light */
}

.form-select-lg:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Fixed width for state dropdown to prevent resizing */
#stateProvince.form-select-lg {
  width: 100%;
  min-width: 200px;
  max-width: 100%;
}

/* Ensure checkboxes also have a compliant border color */
.form-check-input {
  border: 1px solid #6c757d;
}
</style>
